import type {
  Module,
  ModuleCode,
  ModuleCondensed,
  ModuleInformation,
  NUSModuleAttributes,
  Semester,
  SemesterData,
  SemesterDataCondensed,
} from 'types/modules';

export const DISPLAY_SEMESTER: Semester = 1;
export const MIN_LEVEL = 1000;
export const MAX_LEVEL = 5000;
export const MIN_UNITS = 0;
export const MAX_UNITS = 4;
const EXCLUDED_ATTRIBUTE_KEYS: (keyof NUSModuleAttributes)[] = ['year', 'ism', 'urop', 'fyp'];
const EXCLUDED_TITLE_PATTERNS = [
  /\bindependent study\b/i,
  /\burop\b/i,
  /undergraduate research opportunities? progra(?:m|mme)/i,
  /final year project/i,
  /honou?rs project/i,
  /honou?rs thesis/i,
];

type EligibilitySubject = Pick<Module | ModuleInformation, 'moduleCode' | 'title'> &
  Partial<Pick<Module | ModuleInformation, 'moduleCredit' | 'attributes' | 'semesterData'>> &
  Partial<Pick<Module | ModuleInformation, 'prerequisite' | 'preclusion'>> &
  Partial<Pick<ModuleCondensed, 'semesters'>>;

function parseModuleLevel(moduleCode: ModuleCode): number | null {
  const match = moduleCode.match(/^[A-Z]{2,4}\s*([1-9])\d{3}[A-Z]{0,3}$/i);
  return match ? Number(match[1]) * 1000 : null;
}

function parseModuleCredit(moduleCredit?: string): number | null {
  if (!moduleCredit) return null;
  const parsed = parseFloat(moduleCredit);
  return Number.isFinite(parsed) ? parsed : null;
}

function hasSemesterOne(subject: EligibilitySubject): boolean {
  if (subject.semesterData) {
    return subject.semesterData.some((semester) => semester.semester === DISPLAY_SEMESTER);
  }

  if (subject.semesters) {
    return subject.semesters.includes(DISPLAY_SEMESTER);
  }

  return false;
}

function hasExcludedAttributes(attributes?: NUSModuleAttributes): boolean {
  if (!attributes) return false;
  return EXCLUDED_ATTRIBUTE_KEYS.some((attribute) => Boolean(attributes[attribute]));
}

function hasExcludedTitle(title: string): boolean {
  return EXCLUDED_TITLE_PATTERNS.some((pattern) => pattern.test(title));
}

export function isEligibleCourse(subject: EligibilitySubject): boolean {
  const moduleLevel = parseModuleLevel(subject.moduleCode);
  const moduleCredit = parseModuleCredit(subject.moduleCredit);

  if (!hasSemesterOne(subject)) return false;
  if (moduleLevel == null || moduleLevel < MIN_LEVEL || moduleLevel > MAX_LEVEL) return false;
  if (moduleCredit == null || moduleCredit < MIN_UNITS || moduleCredit > MAX_UNITS) return false;
  if (subject.prerequisite) return false;
  if (subject.preclusion) return false;
  if (hasExcludedAttributes(subject.attributes)) return false;
  if (hasExcludedTitle(subject.title)) return false;

  return true;
}

export function filterDisplaySemesterData<T extends SemesterData | SemesterDataCondensed>(
  semesterData: readonly T[],
): T[] {
  return semesterData.filter((semester) => semester.semester === DISPLAY_SEMESTER);
}

export function getDisplayModuleInformation(module: ModuleInformation): ModuleInformation {
  return {
    ...module,
    semesterData: filterDisplaySemesterData(module.semesterData),
  };
}

export function getDisplayModule(module: Module): Module {
  return {
    ...module,
    semesterData: filterDisplaySemesterData(module.semesterData),
  };
}

export function getDisplayModuleCondensed(module: ModuleCondensed): ModuleCondensed {
  return {
    ...module,
    semesters: module.semesters.filter((semester) => semester === DISPLAY_SEMESTER),
  };
}

export function getEligibleDisplayModuleList(moduleList: ModuleCondensed[]): ModuleCondensed[] {
  return moduleList
    .filter((module) => hasSemesterOne(module))
    .map((module) => getDisplayModuleCondensed(module));
}

export const ELIGIBLE_COURSE_SEARCH_FILTER = {
  bool: {
    filter: [
      {
        nested: {
          path: 'semesterData',
          query: {
            term: {
              'semesterData.semester': DISPLAY_SEMESTER,
            },
          },
        },
      },
      {
        range: {
          moduleCredit: {
            gte: MIN_UNITS,
            lte: MAX_UNITS,
          },
        },
      },
      {
        range: {
          'moduleCode.level': {
            gte: MIN_LEVEL,
            lte: MAX_LEVEL,
          },
        },
      },
    ],
    must_not: [
      {
        exists: {
          field: 'prerequisite',
        },
      },
      {
        exists: {
          field: 'preclusion',
        },
      },
      {
        terms: {
          'moduleAttributes.keyword': EXCLUDED_ATTRIBUTE_KEYS,
        },
      },
      { match_phrase: { title: 'independent study' } },
      { match_phrase: { title: 'urop' } },
      { match_phrase: { title: 'undergraduate research opportunities programme' } },
      { match_phrase: { title: 'undergraduate research opportunities program' } },
      { match_phrase: { title: 'final year project' } },
      { match_phrase: { title: 'honours project' } },
      { match_phrase: { title: 'honors project' } },
      { match_phrase: { title: 'honours thesis' } },
      { match_phrase: { title: 'honors thesis' } },
    ],
  },
};
